import React from "react";
import { Plus, Minus, Info } from "lucide-react";
import api from "../../services";
import { useAuth } from "../../context/AuthContext";
import { useOrder } from "../../context/OrderContext";
import { saveGuestCart } from "../../lib/guestCart";
 
// Types
interface MenuItem {
  _id: string;
  name: string;
  restaurant: string;
  quantity: number;
  isVeg: boolean;
  customizations?: string[];
  image?: string;
  requestedCustomization?: string;
  description: string;
  price: number;
  category: string;
  imageUrl?: string;
  customize: CustomizeGroup[];
  customOptions?: {
    label: string;
    price: number;
    qty: number;
    isDefault?: boolean;
    groupName?: string;
  }[];
  tags?: string[];
  cartId: string;
  tax?: number;
  gst?: number;
  pst?: number;
}
 
interface CustomizeGroup {
  name: string;
  required: boolean;
  multiple?: boolean;
  choices: CustomizeChoice[];
}
interface CustomizeChoice {
  label: string;
  price: number;
  image?: string;
  default?: boolean;
}
 
const MenuItemCard: React.FC<{
  item: MenuItem;
  isBonusCovered?: boolean;
  tooltipBottom?: boolean;
}> = ({ item, isBonusCovered = false, tooltipBottom = false }) => {
  const { user, currencySymbol } = useAuth();
  const { orderData, setOrderData } = useOrder();
  const cart = orderData?.items || [];
 
  const syncToDB = async (updatedOrderData: typeof orderData) => {
    // ✅ Sync with backend using the updated data
    try {
      const userId = user?.id;
      if (!userId) {
        saveGuestCart(updatedOrderData);
        return;
      }
 
      const payload = {
        cart: updatedOrderData,
      };
 
      await api.post(`/cart`, payload);
      console.log("✅ Custom item added/updated in cart successfully");
    } catch (error) {
      console.error("❌ Failed to sync custom cart item:", error);
    }
  };
  

  const removeFromCart = (cartId: string) => {
    const newCart = cart.filter((c) => c.cartId !== cartId);
    const updatedData = { ...orderData, items: newCart };
    setOrderData((prev) => ({ ...prev, items: newCart }));
    syncToDB(updatedData);
  };
 
  const updateQuantity = (cartId: string, quantity: number) => {
    console.log(`Cart`, cart);
    if (quantity < 1) return removeFromCart(cartId);
    console.log(`CartIs`, cartId);
    const newCart = cart.map((c) =>
      c.cartId === cartId ? { ...c, quantity } : c
    );
    console.log(`NewCart`, newCart);
    const updatedData = { ...orderData, items: newCart };
    setOrderData((prev) => ({ ...prev, items: newCart }));
    syncToDB(updatedData);
  };

  // Calculate prices — bonus-covered items have no tax
  const basePrice = item.price * item.quantity;
  const taxAmount = isBonusCovered ? 0 : (item.tax || 0) * item.quantity;
  const totalPrice = basePrice + taxAmount;
  // Canadian split; PST takes the rounding remainder so the rows add up
  const hasTaxSplit = !isBonusCovered && (item.gst != null || item.pst != null);
  const gstAmount = hasTaxSplit ? Number(((item.gst || 0) * item.quantity).toFixed(2)) : 0;
  const pstAmount = hasTaxSplit ? Number((taxAmount - gstAmount).toFixed(2)) : 0;
  const customOptions = item.customOptions || [];
  const specialRequestText = item.requestedCustomization?.trim() || "";

  return (
    <div className="flex gap-4 p-2 py-3 bg-white rounded-lg mx-2 border-[1px] border-black/10 dm-cart-item-card">
      {item.image && (
        <img
          src={item.image}
          alt={item.name}
          className="w-14 h-14 object-cover rounded-md flex-shrink-0"
        />
      )}
 
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {/* <FoodTypeIndicator isVeg={item.isVeg} /> */}
              <h4
                className="font-medium text-gray-900 text-sm truncate mb-0"
                title={item.name}
              >
                {item.name}
              </h4>
            </div>
          </div>
 
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex dm-white-btn items-center border border-gray-300 rounded">
              <button
                className="p-1"
                onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
              >
                <Minus className="w-3.5 h-3.5 text-gray-600" />
              </button>
              <span className="px-2 py-1 text-xs font-medium">
                {String(item.quantity)}
              </span>
              <button
                className="p-1"
                onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
              >
                <Plus className="w-3.5 h-3.5 text-gray-600" />
              </button>
            </div>
            
            {/* Price with Info Icon and Tooltip */}
            <div 
              className="relative flex items-center gap-1 group"
            >
              {/* Pre-tax, matching the cart and Item Total; taxes are in the tooltip */}
              <span className="font-medium text-gray-900 text-sm min-w-[50px] text-right">
                {currencySymbol}{basePrice.toFixed(2)}
              </span>
              <Info className="w-3.5 h-3.5 text-gray-600 cursor-help" />
              
              {/* Tooltip */}
              <div className={`pointer-events-none absolute right-0 hidden w-48 bg-white border border-black/10 rounded-lg shadow-lg p-3 z-30 dm-bill-card group-hover:block ${
                tooltipBottom ? "top-full mt-1" : "bottom-full mb-2"
              }`}>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between text-gray-600">
                      <span>Item total</span>
                      <span>{currencySymbol}{basePrice.toFixed(2)}</span>
                    </div>
                    {!isBonusCovered && !hasTaxSplit && (
                      <div className="flex justify-between text-gray-600">
                        <span>Taxes & charges</span>
                        <span>{currencySymbol}{taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {hasTaxSplit && (
                      <>
                        <div className="flex justify-between text-gray-600">
                          <span>GST</span>
                          <span>{currencySymbol}{gstAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>PST</span>
                          <span>{currencySymbol}{pstAmount.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="border-t border-black/10 pt-1.5 mt-1.5"></div>
                    <div className="flex justify-between font-semibold text-gray-900">
                      <span>Total</span>
                      <span>{currencySymbol}{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  {/* Triangle pointer */}
                  <div className={`absolute right-4 w-2 h-2 bg-white border-black/10 transform rotate-45 ${
                    tooltipBottom ? "-top-1 border-l border-t" : "-bottom-1 border-r border-b"
                  }`}></div>
                </div>
            </div>
          </div>
        </div>
        
{(customOptions.length > 0 || Boolean(specialRequestText)) && (
  <div className="mt-1 text-xs text-gray-600">
    <div className="h-[50px] w-[150px] scrollbar-thin overflow-y-auto">
      <span className="font-medium">
        {(() => {
          const removed = customOptions.filter(opt => opt.qty === -1);
          const added = customOptions
            .map((opt) => {
              const rawQty = Number(opt.qty || 0);
              if (rawQty <= 0) return null;

              const baseLabel = String(opt.label || "")
                .replace(/\s+(left|right|whole)$/i, "")
                .trim();
              const matchedChoice = item.customize
                ?.flatMap((group) => group.choices || [])
                ?.find((choice) => choice.label === baseLabel);

              const isDefaultChoice = Boolean(
                opt.isDefault ?? matchedChoice?.default,
              );
              const displayQty = isDefaultChoice ? Math.max(0, rawQty - 1) : rawQty;

              if (displayQty <= 0) return null;
              return { ...opt, qty: displayQty };
            })
            .filter(
              (opt): opt is {
                label: string;
                price: number;
                qty: number;
                isDefault?: boolean;
              } => opt !== null,
            );
          const specialRequest = specialRequestText
            ? `Special requests: ${specialRequestText}`
            : "";
          const parts: string[] = [];
          if (added.length) {
            parts.push(
              `Added: ${added
                .map(opt => `${opt.label}${opt.qty > 1 ? ` ×${opt.qty}` : ""}`)
                .join(", ")}`
            );}
          if (removed.length) {
            parts.push(
              `Removed: ${removed.map(opt => opt.label).join(", ")}`
            );}
            if(specialRequest)
            parts.push(specialRequest);
          return parts.join(" | ");
        })()}
      </span>
    </div>
  </div>
)}
        {item.customizations && (
          <button className="text-[10px] text-gray-500 mt-1 hover:text-gray-700">
            💬 Any suggestions?
          </button>
        )}
      </div>
    </div>
  );
};
 
export default MenuItemCard;
